import React from 'react';
import { Badge, Col, Row } from 'react-bootstrap';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/joy/Grid';
import Stack from '@mui/joy/Stack';
import { FieldLabelRow } from './LabelUserpilotHook';

interface Props {
  id?: string;
  hidden?: boolean;
  name?: string;
  values?: any;
  onChange?: any;
  options: any[];
  mdCol?: number;
  disabled?: boolean;
  className?: string;
  label?: string;
  hiddenFormLabel?: boolean;
  onClear?: any;
  logo?: string;
  helperText?: string;
  isMissing?: any;
  justifyContent?: string;
  verticalLayout?: boolean;
  columns?: number;
  gridGap?: any;
  /** Stable Userpilot spotlight hook. Do not derive from class names. */
  dataUp?: string;
}

const GroupRadioButton = (props: Props) => {
  const handleClear = () => {
    props.onClear && props.onClear();
  };

  let gridGap = {};
  if (props.gridGap !== undefined) {
    gridGap = { gap: `${props.gridGap}` };
  }

  const showLabel =
    Boolean(props.label) && (props.hiddenFormLabel === undefined || props.hiddenFormLabel === false);

  return props.hidden ? null : (
    <div
      className={['userpilot-field-root', props.className].filter(Boolean).join(' ')}
      data-up={showLabel ? undefined : props.dataUp}
      style={{
        border: props.isMissing ? '1px solid #DC2626' : 'none',
        borderRadius: '8px',
        backgroundColor: props.isMissing ? '#FEF2F2' : 'transparent',
      }}
    >
      {showLabel && (
        <FieldLabelRow
          label={props.label}
          logo={props.logo}
          dataUp={props.dataUp}
          isMissing={props.isMissing}
        />
      )}

      {props.helperText && (
        <FormHelperText sx={{ fontSize: '12px', color: '#6B7280' }}>
          {props.helperText}
        </FormHelperText>
      )}

      <Stack direction="column" spacing={0.5}>
        <RadioGroup
          name={props.name}
          value={props.values ?? ''}
          onChange={props.onChange}
          className="mb-3"
        >
          <Grid
            container
            rowSpacing={1.25} /* Increased vertical gap between grid rows */
            justifyContent={props.justifyContent || 'flex-start'}
            sx={gridGap}
          >
            {(props.options || []).map((opt, index) => {
              const optValue = opt?.value || opt;
              const optLabel = opt?.label || opt?.value || opt;
              const isSelected = optValue === props.values;

              return (
                <Grid
                  key={optValue || index}
                  xs={12}
                  md={props.columns ? 12 / props.columns : props.verticalLayout ? 12 : 'auto'}
                  sx={{ padding: '0px!important', marginBottom: '4px' }} /* Added subtle bottom margin per option */
                >
                  <FormControlLabel
                    value={optValue}
                    control={
                      <Radio
                        size="medium"
                        sx={{
                          color: props.isMissing ? '#DC2626' : '#3B82F6',
                          /* Reduce surrounding padding to align smoothly with text */
                          padding: '4px 8px',
                          '&.Mui-checked': {
                            color: props.isMissing ? '#DC2626' : '#3B82F6',
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(59, 130, 246, 0.04)',
                          },
                        }}
                      />
                    }
                    label={optLabel}
                    disabled={props.disabled}
                    sx={{
                      width: 'auto',
                      maxWidth: '100%',
                      minWidth: 0,
                      margin: 0,
                      /* Center align radio icon and text horizontally */
                      display: 'inline-flex',
                      alignItems: 'center',
                      '& .MuiFormControlLabel-label': {
                        fontSize: '14px',
                        color: '#374151',
                        lineHeight: 1, /* Remove extra top/bottom line height spacing */
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        display: 'inline-block',
                        margin: 0,
                        padding: 0,
                      },
                      '& .MuiRadio-root': {
                        margin: 0,
                        paddingTop: '4px',
                        paddingBottom: '4px',
                      },
                    }}
                  />

                  {/* Render nested extension element below when selected */}
                  {opt?.extendElement && isSelected && (
                    <div style={{ marginTop: '6px', paddingLeft: '32px' }}>
                      {opt.extendElement}
                    </div>
                  )}
                </Grid>
              );
            })}
          </Grid>
        </RadioGroup>

        {props.onClear && (
          <Row>
            <Col md={props.mdCol || 3}>
              <Badge
                bg="light"
                text="dark"
                className="pointer"
                onClick={props.disabled ? undefined : handleClear}
                style={{
                  backgroundColor: props.disabled ? '#F5F5F5' : '#E3F2FD',
                  color: props.disabled ? '#9E9E9E' : '#1976D2',
                  border: props.disabled ? '1px solid #E0E0E0' : '1px solid #BBDEFB',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: props.disabled ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'inline-block',
                  marginTop: '8px',
                  opacity: props.disabled ? 0.6 : 1,
                }}
                onMouseEnter={
                  props.disabled
                    ? undefined
                    : (e) => {
                        const target = e.target as HTMLElement;
                        target.style.backgroundColor = '#BBDEFB';
                        target.style.color = '#1565C0';
                      }
                }
                onMouseLeave={
                  props.disabled
                    ? undefined
                    : (e) => {
                        const target = e.target as HTMLElement;
                        target.style.backgroundColor = '#E3F2FD';
                        target.style.color = '#1976D2';
                      }
                }
              >
                Clear
              </Badge>
            </Col>
          </Row>
        )}
      </Stack>

      {props.isMissing && (
        <FormHelperText sx={{ color: '#DC2626', fontSize: '12px', marginTop: '4px' }}>
          This field is required.
        </FormHelperText>
      )}
    </div>
  );
};

export default GroupRadioButton;